import { notEmpty } from '@mahawi/eshop-common/src/not-empty';
import { numberCompare } from '@mahawi/eshop-common/src/number-compare';
import { stringCompare } from '@mahawi/eshop-common/src/string-compare';
import {
  getLowerCaseLanguageCode,
  getTranslation,
} from '@mahawi/eshop-common/src/translation';
import {
  EProductScoringDays,
  type ICurrencyType,
  type IProductFeatureFlag,
  type IReportProductScoring,
} from '@mahawi/eshop-common/src/types';
import { Button, Popover, Select, Space, Tag } from 'antd';
import { type ColumnGroupType, type ColumnType } from 'antd/es/table';
import React, { type Key, useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import DetailPageHeader from '../components/detail-page-header';
import ExternalLink from '../components/external-link';
import Link from '../components/link';
import Table from '../components/table';
import { featureFlagTags } from '../fragments/feature-flag-tags';
import { getCountryOptions } from '../fragments/get-country-options';
import { getTableFeatureFlagsColumnFilters } from '../fragments/table-feature-flags-column';
import { getColumnSearch } from '../fragments/table-get-column-search';
import {
  getCalculatedEndPriceMarginColumn,
  type ProductTableColumnCalculatedEndPriceMargin,
} from '../fragments/table-row-product-calculated-end-price-margin';
import { type RootState } from '../reducers';
import { reportProductScoringLoad } from '../reducers/report/reducer';
import { SPACING } from '../theme/tokens';

interface TableColumn
  extends IReportProductScoring, ProductTableColumnCalculatedEndPriceMargin {
  key: string;
  name: string | null;
}

function ReportProductScoring(): React.ReactElement {
  const dispatch = useDispatch();
  const Report = useSelector((state: RootState) => state.Report);
  const Config = useSelector((state: RootState) => state.Config);
  const Language = useSelector((state: RootState) => state.Language);
  const Countries = useSelector((state: RootState) => state.Countries);
  const FeatureFlags = useSelector((state: RootState) => state.FeatureFlags);
  const Warehouse = useSelector((state: RootState) => state.Warehouse);
  const Currency = useSelector((state: RootState) => state.Currency);

  const [dayInterval, setDayInterval] = useState<EProductScoringDays>(7);
  const [countryFilter, setCountryFilter] = useState<string | undefined>(
    undefined,
  );

  const currencyTypeEUR: ICurrencyType | undefined = Currency.currencies.find(
    (c: ICurrencyType): boolean => c.isoCode === 'EUR',
  );

  if (!currencyTypeEUR) {
    throw new Error('EUR currency not found');
  }

  const dataSource = useMemo<TableColumn[]>((): TableColumn[] => {
    return (
      Report.productScoring?.map(
        (item: IReportProductScoring): TableColumn => ({
          key: item.product.uuid,
          name: getTranslation(item.translation.names, Language.languageType),
          ...item,
        }),
      ) ?? []
    );
  }, [Report.productScoring, Language.languageType]);

  const brandFilters = useMemo(() => {
    return [
      ...new Set(
        dataSource.map(
          ({ brand }: TableColumn): string | undefined => brand?.name,
        ),
      ),
    ]
      .filter(notEmpty)
      .map((name: string) => ({
        text: name,
        value: name,
      }))
      .sort((a, b): number => stringCompare(a.value, b.value));
  }, [dataSource]);

  const renderProductName = useCallback(
    (text: string, record: TableColumn): React.ReactElement => (
      <Space align="start" size={SPACING.sm}>
        <Link
          to={`/product/${record.product.uuid}`}
          label={text}
          openInNewTab
        />
        <ExternalLink
          to={`${
            Config.url
          }/${getLowerCaseLanguageCode(Language.languageType)}/product/${record.product.uuid}`}
          ariaLabel="Open product in new tab"
        />
      </Space>
    ),
    [Config.url, Language.languageType],
  );

  const columns = useMemo(
    () => [
      {
        title: 'Brand',
        dataIndex: ['brand', 'name'],
        sorter: (a: TableColumn, b: TableColumn): number =>
          stringCompare(a.brand.name, b.brand.name),
        filterSearch: true,
        filters: brandFilters,
        onFilter: (value: boolean | Key, record: TableColumn): boolean =>
          record.brand?.name === value,
        width: '5%',
      },
      {
        title: 'Name',
        dataIndex: 'name',
        render: renderProductName,
        sorter: (a: TableColumn, b: TableColumn): number =>
          stringCompare(a.name, b.name),
        width: '20%',
        ...getColumnSearch('name'),
      },
      getCalculatedEndPriceMarginColumn(currencyTypeEUR),
      {
        title: 'Views',
        dataIndex: ['productScoring', 'views'],
        sorter: (a: TableColumn, b: TableColumn): number =>
          numberCompare(a.productScoring.views, b.productScoring.views),
        width: '5%',
      },
      {
        title: 'Basket',
        dataIndex: ['productScoring', 'basketCount'],
        sorter: (a: TableColumn, b: TableColumn): number =>
          numberCompare(
            a.productScoring.basketCount,
            b.productScoring.basketCount,
          ),
        width: '5%',
      },
      {
        title: 'Order',
        dataIndex: ['productScoring', 'orderCount'],
        sorter: (a: TableColumn, b: TableColumn): number =>
          numberCompare(
            a.productScoring.orderCount,
            b.productScoring.orderCount,
          ),
        width: '5%',
      },
      {
        title: 'Score CPS',
        dataIndex: ['productScoring', 'scoreCPS'],
        sorter: (a: TableColumn, b: TableColumn): number =>
          numberCompare(a.productScoring.scoreCPS, b.productScoring.scoreCPS),
        width: '5%',
      },
      {
        title: 'Score RPS',
        dataIndex: ['productScoring', 'scoreRPS'],
        sorter: (a: TableColumn, b: TableColumn): number =>
          numberCompare(a.productScoring.scoreRPS, b.productScoring.scoreRPS),
        width: '5%',
      },
      {
        title: 'Score AES',
        dataIndex: ['productScoring', 'scoreAES'],
        sorter: (a: TableColumn, b: TableColumn): number =>
          numberCompare(a.productScoring.scoreAES, b.productScoring.scoreAES),
        width: '5%',
      },
      {
        title: 'Predicated ROAS',
        dataIndex: ['productScoring', 'predicatedROAS'],
        sorter: (a: TableColumn, b: TableColumn): number =>
          numberCompare(
            a.productScoring.predicatedROAS,
            b.productScoring.predicatedROAS,
          ),
        width: '5%',
      },
      {
        title: 'Inventory risk factor',
        dataIndex: ['productScoring', 'inventoryRiskFactor'],
        sorter: (a: TableColumn, b: TableColumn): number =>
          numberCompare(
            a.productScoring.inventoryRiskFactor,
            b.productScoring.inventoryRiskFactor,
          ),
        width: '5%',
      },
      {
        title: 'Ads score',
        dataIndex: ['productScoring', 'adsScore'],
        sorter: (a: TableColumn, b: TableColumn): number =>
          numberCompare(a.productScoring.adsScore, b.productScoring.adsScore),
        width: '5%',
      },
      {
        title: 'Attractiveness',
        dataIndex: ['productScoring', 'attractivenessScore'],
        sorter: (a: TableColumn, b: TableColumn): number =>
          numberCompare(
            a.productScoring.attractivenessScore,
            b.productScoring.attractivenessScore,
          ),
        render: (score: number | null | undefined): React.ReactElement => {
          if (score == null) return <>-</>;
          const color = score >= 70 ? 'green' : score >= 40 ? 'yellow' : 'red';
          return <Tag color={color}>{score}</Tag>;
        },
        width: 130,
      },
      {
        title: 'Feature flags',
        dataIndex: 'featureFlags',
        sorter: (a: TableColumn, b: TableColumn): number =>
          a.featureFlags.length - b.featureFlags.length,
        filterSearch: true,
        filters: getTableFeatureFlagsColumnFilters({
          dataSource,
          FeatureFlags,
        }),
        onFilter: (value: boolean | Key, record: TableColumn): boolean => {
          if (value === 'withFeatureFlags') {
            return record.featureFlags.length > 0;
          }

          if (value === 'withoutFeatureFlags') {
            return record.featureFlags.length === 0;
          }

          return record.featureFlags.some(
            (productFeatureFlag: IProductFeatureFlag): boolean => {
              return productFeatureFlag.code === value;
            },
          );
        },
        render: (
          productFeatureFlags: IProductFeatureFlag[],
        ): React.ReactElement[] =>
          featureFlagTags({ productFeatureFlags, FeatureFlags, Warehouse }),
        width: '5%',
      },
    ],
    [brandFilters, renderProductName],
  );

  return (
    <>
      <DetailPageHeader
        title="Report product scoring"
        onReload={(): void => {
          if (!countryFilter) {
            return;
          }

          dispatch(reportProductScoringLoad(dayInterval, countryFilter));
        }}
        isReloadDisabled={Report.inProcess || !countryFilter}
        isReloadHighlighted={!Report.productScoring}
      />

      <Space
        size={SPACING.lg}
        orientation="horizontal"
        style={{ width: '100%' }}
      >
        <Popover
          trigger="hover"
          content="Select country for report"
          placement="top"
        >
          <Select
            defaultValue={countryFilter}
            style={{ width: 240 }}
            onChange={setCountryFilter}
            options={getCountryOptions(Countries.countries)}
          />
        </Popover>

        <Popover
          trigger="hover"
          content="Select interval in days"
          placement="top"
        >
          <Space size={SPACING.sm}>
            {Object.values(EProductScoringDays)
              .filter(
                (v: string | EProductScoringDays): v is EProductScoringDays =>
                  typeof v === 'number',
              )
              .map((interval: EProductScoringDays): React.ReactElement => (
                <Button
                  key={interval}
                  type={dayInterval === interval ? 'primary' : 'default'}
                  onClick={(): void => setDayInterval(interval)}
                >
                  {interval} days
                </Button>
              ))}
          </Space>
        </Popover>
      </Space>

      <Table
        columns={columns as (ColumnGroupType<object> | ColumnType<object>)[]}
        dataSource={dataSource}
        isLoading={Report.inProcess}
        emptyText="No scoring data found"
      />
    </>
  );
}

export default ReportProductScoring;
